import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";

const Chip = ({ children, tone = "sage" }) => (
  <span className={`rounded-full px-2 py-0.5 text-[11px] ${tone === "sage" ? "bg-[#E8F0EC] text-[#3E6B5C]" : "bg-[#F6E7DF] text-[#C05630]"}`}>{children}</span>
);

const ClusterRow = ({ token, cl, kids, pass, initiallyOpen, focused }) => {
  const [open, setOpen] = useState(initiallyOpen);
  const ref = useRef(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [focused]);
  const shown = kids.filter(pass);
  return (
    <li ref={ref} className="scroll-mt-24 border-b border-[#1C242B]/10 last:border-b-0" data-testid={`list-cluster-${cl.id}`}>
      <button onClick={() => setOpen(!open)} data-testid={`list-cluster-toggle-${cl.id}`} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-[#FAF8F5] transition-colors">
        <span className="w-2 h-9 rounded-full shrink-0" style={{ background: cl.color }} />
        <span className="flex-1 min-w-0">
          <span className="block font-display font-semibold text-[#1C242B]">{cl.label}</span>
          <span className="block text-xs text-slate-500">{shown.length} of {kids.length} careers match filters</span>
        </span>
        <span className="font-mono-ui text-sm text-[#D96B43]">{cl.score}%</span>
        <ChevronDown size={18} className={`text-slate-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="px-5 pb-4 grid gap-2.5 fade-up">
          {shown.length === 0 && <li className="text-sm text-slate-500">No careers here match your filters.</li>}
          {shown.map((c) => (
            <li key={c.id}>
              <Link to={`/results/${token}/career/${c.id}`} data-testid={`list-career-${c.id}`} className="block rounded-xl border border-[#1C242B]/10 bg-white p-4 hover:border-[#D96B43]/50 transition-colors">
                <div className="flex justify-between gap-3">
                  <span className="font-medium text-[#1C242B]">{c.title}</span>
                  <span className="font-mono-ui text-sm font-semibold text-[#D96B43] shrink-0">{c.score}%</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{c.reasons[0]}</p>
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  <Chip>Public: {c.public}</Chip><Chip>People: {c.people}</Chip><Chip>{c.setting}</Chip>
                  {c.remote && c.remote !== "On-site" && <Chip tone="terra">{c.remote}</Chip>}
                </div>
                <span className="inline-flex items-center gap-1 text-xs text-[#3E6B5C] mt-2.5">Details &amp; next steps <ArrowUpRight size={12} /></span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export const CareerListView = ({ token, clusters, byId, pass, focusId }) => (
  <ul data-testid="career-list-view" className="divide-y-0">
    {clusters.map((cl, i) => (
      <ClusterRow key={cl.id} token={token} cl={cl} kids={cl.careers.map((id) => byId[id])} pass={pass} initiallyOpen={focusId ? cl.id === focusId : i === 0} focused={cl.id === focusId} />
    ))}
  </ul>
);
